import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router';
import { formatCurrency } from 'utils/l10n';

import { Modal, Button } from 'common/lazy';
import { useClassName, useWindowSize } from 'common/hooks';
import { SVGIcon } from 'common/components';
import { BottomSheet } from 'public/components/BottomSheet';
import { parseAttraction } from 'public/helpers/contentful/parser';
import { track } from 'utils/analytics';
import { ContentfulRichText, ContentfulImage } from 'common/components';

import './ticket-upgrade-modal.less';

function getQuantity({ quantities }) {
  const ticketOptionId = Object.keys(quantities)[0];
  if (!ticketOptionId) return null;

  const quantity = quantities[ticketOptionId].quantity;

  return quantity;
}

function getTicketPrice({ quantities, inventory }) {
  const ticketOptionId = Object.keys(quantities)[0];
  if (!ticketOptionId) return null;

  const item = inventory.find((a) => a.ticketOptionId === ticketOptionId);
  if (!item) return null;

  return item.price;
}

function getUpgrade(venue) {
  if (!venue) return null;

  const attractionScreen = parseAttraction(venue.content.attractionScreen);
  const { upgrade } = attractionScreen;

  return upgrade;
}

const TicketUpgradeModal = ({
  venue,
  bundle,
  inventory,
  isOpen,
  quantities,
  selectedDate,
  onClose,
  onContinue,
}) => {
  const { isMobile } = useWindowSize();
  const classNames = useClassName('TicketUpgradeModal');
  const [redirect, setRedirect] = useState(false);

  if (!venue) return null;
  if (!bundle) return null;

  const upgrade = getUpgrade(venue);
  if (!upgrade) return null;

  const url = `/bundle/${bundle.slug}?date=${selectedDate}&venue=${
    venue.slug
  }&quantity=${getQuantity({ quantities })}`;

  if (redirect) {
    return <Redirect to={url} push />;
  }

  const content = (
    <div className={classNames('item')}>
      <ContentfulImage
        field={
          isMobile ? upgrade.asset.fields.mobile : upgrade.asset.fields.desktop
        }
      />
      <ContentfulRichText
        field={upgrade.description}
        args={{
          balance: formatCurrency(
            bundle.amount.totalAmountWithoutTaxes * 100 -
              getTicketPrice({ quantities, inventory })
          ),
        }}
      />
      <div className={classNames('actions')}>
        <Button
          primary
          onClick={() => {
            track('Accept Upgrade', {
              url: upgrade.url,
              venueSlug: venue.slug,
              date: selectedDate,
              quantity: getQuantity({ quantities }),
              origin: location.pathname,
            });
            setRedirect(true);
          }}>
          Upgrade now!
        </Button>
        <Button
          onClick={() => {
            track('Decline Upgrade', {
              url: upgrade.url,
              venueSlug: venue.slug,
              date: selectedDate,
              quantity: getQuantity({ quantities }),
              origin: location.pathname,
            });
            onContinue();
          }}>
          Continue with my order
        </Button>
      </div>
    </div>
  );

  const onCloseHandler = () => {
    track('Close Upgrade', {
      url: upgrade.url,
      venueSlug: venue.slug,
      date: selectedDate,
      quantity: getQuantity({ quantities }),
      origin: location.pathname,
    });
    onClose();
  };

  if (isMobile) {
    return (
      <BottomSheet
        onClose={onCloseHandler}
        isOpen={isOpen}
        title={upgrade.title}
        className={classNames('container')}
        open={open}
        content={<div className={classNames('content')}>{content}</div>}
      />
    );
  }

  return (
    <Modal
      onClose={onCloseHandler}
      open={isOpen}
      size="small"
      className={classNames('container')}>
      <Modal.Content className={classNames('content')}>
        <div className={classNames('header')}>
          <h1>{upgrade.title}</h1>
          <div className={classNames('close')} onClick={onCloseHandler}>
            <SVGIcon name="close" />
          </div>
        </div>
        {content}
      </Modal.Content>
    </Modal>
  );
};

TicketUpgradeModal.propTypes = {
  venue: PropTypes.object,
  isOpen: PropTypes.bool,
  quantities: PropTypes.object,
  selectedDate: PropTypes.string,
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
};

export default TicketUpgradeModal;
